import type { Circuit, PlayerTrackPosition } from '../types/track.js';
import { isStartSideOnSfOverlap, hasStartFinishCellOverlap } from './circuitLoop.js';
import { normalizeMainPathIndex } from './jokerLap.js';
import { hasStartLane } from './startLane.js';

export interface LapDisplayContext {
  totalDiceRolls?: number;
  skipStartLineCrossingsRemaining?: number;
  hadStartLineSkipAtStart?: boolean;
  startSideOnSfOverlap?: boolean;
  /** Fin de manche : tous les actifs ont le même nb de lancers (> 0). */
  uniformDiceRollsAmongField?: boolean;
}

/** Tours comptabilisés + inférence sur boucle S/F (ligne d’arrivée / 2e tour après skip). */
export function standingCompletedLapsOnCircuit(
  circuit: Circuit | null | undefined,
  position: PlayerTrackPosition | undefined,
  lapCount: number,
  options?: LapDisplayContext
): number {
  if (!circuit || !position) return lapCount;
  const pathLen = circuit.path.length;
  if (pathLen < 2) return lapCount;
  if (position.jokerBranchIndex != null || position.startLaneIndex != null) return lapCount;

  const idx = normalizeMainPathIndex(position.mainPathIndex, pathLen);
  let standing = lapCount;

  // Ligne d’arrivée / case départ après wrap : valable sur tout circuit bouclé (pas seulement overlap S/F strict).
  if (idx === pathLen - 1) {
    standing = Math.max(standing, lapCount + 1);
  } else if (idx === 0 && (options?.totalDiceRolls ?? 0) > 0 && lapCount > 0) {
    standing = Math.max(standing, lapCount + 1);
  } else if (
    hasStartFinishCellOverlap(circuit) &&
    idx === 0 &&
    (options?.totalDiceRolls ?? 0) > 0 &&
    lapCount === 0 &&
    (options?.startSideOnSfOverlap === true || options?.uniformDiceRollsAmongField === true)
  ) {
    const join = circuit.startLane?.joinPathIndex;
    const onStartLaneJoin = hasStartLane(circuit) && (join === 0 || join === pathLen - 1);
    const skipConsumedOpening =
      options?.hadStartLineSkipAtStart === true &&
      (options?.skipStartLineCrossingsRemaining ?? 1) === 0;
    // Jonction S/F après un départ grille : pas un tour bouclé.
    if (!onStartLaneJoin || skipConsumedOpening) {
      standing = Math.max(standing, 1);
    }
  }

  // Skip S/F consommé (demi/finale) : le HUD affiche le 2e tour, mais le
  // classement live reste sur pathLen+idx (voir trackProgress) — ne pas
  // réutiliser ce standing pour gonfler le progressScore.
  const skipConsumed =
    options?.hadStartLineSkipAtStart === true &&
    (options?.skipStartLineCrossingsRemaining ?? 1) === 0 &&
    (options?.totalDiceRolls ?? 0) > 0 &&
    lapCount === 0;
  if (skipConsumed) {
    standing = Math.max(standing, 1);
  }

  return standing;
}

/** Tours complétés (0-based) → tour en cours affiché (1-based). */
export function displayLapCurrent(
  completedLaps: number,
  numLaps: number,
  finished: boolean
): number {
  if (finished) return numLaps;
  return Math.min(completedLaps + 1, numLaps);
}

export interface RaceLapIndicatorContext {
  totalDiceRolls?: readonly number[];
  skipStartLineCrossingsRemaining?: readonly number[];
  hadStartLineSkipAtStart?: readonly boolean[];
}

/**
 * Tour de course affiché (1/N) pour le HUD global.
 * Suit le pilote le plus avancé en tours complétés (évite un 1/N quand le leader est déjà au tour suivant).
 */
export function raceLapIndicatorFromField(
  completedLaps: readonly number[],
  finished: readonly boolean[],
  numLaps: number,
  circuit?: Circuit | null,
  positions?: readonly PlayerTrackPosition[],
  context?: RaceLapIndicatorContext
): { current: number; total: number } {
  const n = completedLaps.length;
  const indices = Array.from({ length: n }, (_, i) => i);
  let maxCompleted = -1;
  for (let i = 0; i < n; i++) {
    if (finished[i]) continue;
    const startSide =
      circuit != null && positions?.[i]
        ? isStartSideOnSfOverlap(
            circuit,
            i,
            positions[i],
            completedLaps[i] ?? 0,
            (j) => positions[j],
            (j) => completedLaps[j] ?? 0,
            indices
          )
        : false;
    const laps = standingCompletedLapsOnCircuit(
      circuit ?? null,
      positions?.[i],
      completedLaps[i] ?? 0,
      {
        totalDiceRolls: context?.totalDiceRolls?.[i],
        skipStartLineCrossingsRemaining: context?.skipStartLineCrossingsRemaining?.[i],
        hadStartLineSkipAtStart: context?.hadStartLineSkipAtStart?.[i],
        startSideOnSfOverlap: startSide,
      }
    );
    if (laps > maxCompleted) maxCompleted = laps;
  }
  if (maxCompleted < 0) {
    return { current: numLaps, total: numLaps };
  }
  return {
    current: displayLapCurrent(maxCompleted, numLaps, false),
    total: numLaps,
  };
}
